import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { RefreshCw, Activity, Database, LogOut, Search, ShoppingCart, User, FileText, CreditCard, Eye, TrendingUp, TrendingDown, AlertCircle, Copy, Check, ChevronUp, ChevronDown, Filter, Key, Gauge, LayoutDashboard, SlidersHorizontal } from 'lucide-react';
import logo from '../assets/images/turret-dark.svg';
import ClusterValuesSheet from './ClusterValuesSheet';
import SegmentsPanel from './SegmentsPanel';
import { buildApiUrl } from '../config/api';
import { buildSegmentQueryParams, type SegmentFilters } from '../api/segments';

interface User {
  id: number;
  email: string;
  name: string;
  organizations: Array<{
    id: number;
    name: string;
    role: string;
    plan: 'trial' | 'pro' | 'business' | 'enterprise';
    events_used: number;
    plan_limit: number;
    events_remaining: number;
    usage_percentage: number;
    trial_active: boolean;
    trial_expired: boolean;
    trial_days_remaining: number;
    trial_ends_at?: string;
    over_limit: boolean;
    approaching_limit: boolean;
    can_track_events: boolean;
    projects: Array<{
      id: number;
      name: string;
      api_key: string;
    }>;
  }>;
}

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: {
    id: number;
    name: string;
  };
}

interface EventType {
  name: string;
  count: number;
}

interface ClusterData {
  id: number;
  label: string;
  key: string;
  size: number;
  delta: number | null;
  sparkline: number[];
  samples: string[];
  event_type: string;
  has_trend_data: boolean;
}

interface ClusterStats {
  total_event_metadata: number;
  clustered_count: number;
  unclustered_count: number;
}

// Time range options for trend analysis
type TrendDays = 7 | 14 | 30;
const TREND_OPTIONS: { value: TrendDays; label: string }[] = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

// Sparkline component for trend visualization
const Sparkline: React.FC<{ data: number[]; positive: boolean }> = ({ data, positive }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min;
  
  return (
    <div className="flex items-end space-x-0.5 h-8 w-16">
      {data.map((value, index) => {
        const height = range === 0 ? 50 : ((value - min) / range) * 100;
        return (
          <div
            key={index}
            className={`flex-1 ${positive ? 'bg-green-500' : 'bg-red-500'} opacity-70 rounded-sm`}
            style={{ height: `${Math.max(height, 10)}%` }}
          />
        );
      })}
    </div>
  );
};

interface DashboardProps {
  user: User;
  token: string;
  onLogout: () => void;
  currentProject: Project | null;
  onRefreshUser: () => Promise<void>;
}

export default function Dashboard({ user, token, onLogout, currentProject, onRefreshUser }: DashboardProps) {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [clusters, setClusters] = useState<ClusterData[]>([]);
  const [clusterStats, setClusterStats] = useState<ClusterStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [sortField, setSortField] = useState<'size' | 'delta' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterKey, setFilterKey] = useState<string>('all');
  const [filterEventType, setFilterEventType] = useState<string>('all');
  const [searchLabel, setSearchLabel] = useState<string>('');
  const [isClusterSheetOpen, setIsClusterSheetOpen] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);
  const [trendDays, setTrendDays] = useState<TrendDays>(7);
  const [isSegmentsPanelOpen, setIsSegmentsPanelOpen] = useState(false);
  const [segmentFilters, setSegmentFilters] = useState<SegmentFilters>({});

  // Fetch event types and clusters when project changes
  useEffect(() => {
    if (currentProject) {
      fetchEventTypes();
      fetchClusters(trendDays, segmentFilters);
      fetchClusterStats();
    } else {
      setEventTypes([]);
      setClusters([]);
      setClusterStats(null);
    }
  }, [currentProject, token]);

  // Refetch clusters when segment filters change
  useEffect(() => {
    if (currentProject) {
      fetchClusters(trendDays, segmentFilters);
    }
  }, [segmentFilters]);

  // Refetch clusters when trend days changes
  const handleTrendDaysChange = (days: TrendDays) => {
    setTrendDays(days);
    fetchClusters(days, segmentFilters);
  };

  // Handle segment filter changes
  const handleSegmentFiltersChange = (filters: SegmentFilters) => {
    setSegmentFilters(filters);
  };

  const fetchEventTypes = async () => {
    if (!currentProject) return;
    
    try {
      setEventTypesLoading(true);
      setError(null);
      
      const response = await fetch(buildApiUrl(`api/v1/projects/${currentProject.id}/events`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch event types');
      }
      
      const data = await response.json();
      setEventTypes(data.data.event_types || []);
    } catch (error) {
      console.error('Error fetching event types:', error);
      setError('Failed to load event types');
      setEventTypes([]);
    } finally {
      setEventTypesLoading(false);
    }
  };

  const fetchClusters = async (days: TrendDays = trendDays, filters: SegmentFilters = segmentFilters) => {
    if (!currentProject) return;

    try {
      setClustersLoading(true);
      setError(null);

      // Build URL with segment filters
      let url = `api/v1/projects/${currentProject.id}/clusters?version=latest&days=${days}`;
      const segmentParams = buildSegmentQueryParams(filters);
      if (segmentParams) {
        url += `&${segmentParams}`;
      }

      // Fetch all clusters with trend data for specified days
      const response = await fetch(buildApiUrl(url), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clusters');
      }

      const data = await response.json();
      setClusters(data || []);
    } catch (error) {
      console.error('Error fetching clusters:', error);
      setError('Failed to load clusters');
      setClusters([]);
    } finally {
      setClustersLoading(false);
    }
  };

  const fetchClusterStats = async () => {
    if (!currentProject) return;
    
    try {
      const response = await fetch(buildApiUrl(`api/v1/projects/${currentProject.id}/clusters/stats`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cluster stats');
      }
      
      const data = await response.json();
      setClusterStats(data);
    } catch (error) {
      console.error('Error fetching cluster stats:', error);
      setClusterStats(null);
    }
  };

  const copyApiKey = async () => {
    if (!currentProject) return;
    
    try {
      await navigator.clipboard.writeText(currentProject.api_key);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };

  const truncateApiKey = (key: string) => {
    if (key.length <= 20) return key;
    return `${key.substring(0, 16)}...`;
  };

  const getUsageInfo = () => {
    if (!user || !user.organizations.length) return null;
    
    const org = user.organizations[0];
    
    return {
      used: org.events_used || 0,
      total: org.plan_limit === Infinity ? null : org.plan_limit,
      remaining: org.events_remaining,
      percentage: org.usage_percentage,
      label: 'Usage',
      subtitle: 'Organization-wide (all projects)',
      isTrialExpiring: org.trial_active && org.trial_days_remaining <= 7,
      isOverLimit: org.over_limit,
      isApproachingLimit: org.approaching_limit,
      canTrackEvents: org.can_track_events,
      plan: org.plan,
      trialDaysRemaining: org.trial_days_remaining,
      trialActive: org.trial_active,
      trialExpired: org.trial_expired
    };
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num == null || num === undefined) {
      return '0';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      fetchEventTypes(),
      fetchClusters(),
      fetchClusterStats(),
      onRefreshUser()
    ]);
    setLoading(false);
  };

  const usageInfo = getUsageInfo();

  const handleSort = (field: 'size' | 'delta') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getUniqueKeys = () => {
    const keys = clusters.map(cluster => cluster.key);
    return Array.from(new Set(keys)).sort();
  };

  const getUniqueEventTypes = () => {
    const types = clusters.map(cluster => cluster.event_type).filter(Boolean);
    return Array.from(new Set(types)).sort();
  };

  const getSortedAndFilteredClusters = () => {
    let filteredClusters = clusters;
    
    // Apply key filtering
    if (filterKey && filterKey !== 'all') {
      filteredClusters = filteredClusters.filter(cluster => 
        cluster.key === filterKey
      );
    }
    
    // Apply event type filtering
    if (filterEventType && filterEventType !== 'all') {
      filteredClusters = filteredClusters.filter(cluster => 
        cluster.event_type === filterEventType
      );
    }
    
    // Apply label search
    if (searchLabel.trim()) {
      filteredClusters = filteredClusters.filter(cluster => 
        cluster.label.toLowerCase().includes(searchLabel.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField) {
      filteredClusters = [...filteredClusters].sort((a, b) => {
        let aValue: number;
        let bValue: number;
        
        if (sortField === 'size') {
          aValue = a.size;
          bValue = b.size;
        } else {
          aValue = a.delta ?? -Infinity;
          bValue = b.delta ?? -Infinity;
        }
        
        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }
    
    return filteredClusters;
  };

  const handleClusterClick = (clusterId: number) => {
    setSelectedClusterId(clusterId);
    setIsClusterSheetOpen(true);
  };

  const handleClusterSheetClose = () => {
    setIsClusterSheetOpen(false);
    setSelectedClusterId(null);
  };

  const SortableHeader = ({ field, children, className = '' }: { field: 'size' | 'delta', children: React.ReactNode, className?: string }) => (
    <TableHead 
      className={`cursor-pointer hover:bg-muted/50 transition-colors select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <div className="flex flex-col">
          <ChevronUp 
            className={`h-3 w-3 transition-colors ${
              sortField === field && sortDirection === 'asc' ? 'text-primary' : 'text-muted-foreground'
            }`} 
          />
          <ChevronDown 
            className={`h-3 w-3 -mt-1 transition-colors ${
              sortField === field && sortDirection === 'desc' ? 'text-primary' : 'text-muted-foreground'
            }`} 
          />
        </div>
      </div>
    </TableHead>
  );

  const sortedAndFilteredClusters = getSortedAndFilteredClusters();
  const uniqueKeys = getUniqueKeys();
  const uniqueEventTypes = getUniqueEventTypes();

  if (!currentProject) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12 text-muted-foreground">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No project selected</h3>
            <p className="text-sm">
              Please select a project from the sidebar to view its dashboard
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4 text-neutral-800">
            <h1 className="text-2xl font-bold flex items-center"><LayoutDashboard className="h-6 w-6 mr-1" /> Dashboard</h1>
            <Badge variant="secondary" className="text-sm">
              {currentProject.name}
            </Badge>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              onClick={refreshData} 
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
            <Button 
              variant="outline" 
              onClick={onLogout}
              className="flex items-center space-x-2"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {usageInfo?.trialActive && (
          <Card className="mb-8 border-blue-200 bg-blue-50">
            <CardContent className="p-3">
              <div className="space-y-2 text-blue-700">
                <div className="font-medium text-sm">Enjoy your free trial! (250K limit)</div>
                <div className="text-blue-600 text-xs">
                  After trial: {usageInfo.plan === 'pro' ? '1M' : usageInfo.plan === 'business' ? '10M' : 'Unlimited'} events per month
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {usageInfo?.isTrialExpiring && (
          <div className="text-xs text-amber-600">
            Trial expires soon! Upgrade to continue tracking events.
          </div>
        )}

        {/* Unclustered Events Info - shown only when there are unclustered events */}
        {clusterStats && clusterStats.unclustered_count > 0 && (
          <div className="mb-6 flex items-center justify-between text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-2.5">
            <div className="flex items-center space-x-2">
              <Database className="h-4 w-4" />
              <span>
                <span className="font-medium text-foreground">{formatNumber(clusterStats.unclustered_count)}</span> events ({((clusterStats.unclustered_count / clusterStats.total_event_metadata) * 100).toFixed(1)}%) don't belong to any topic
              </span>
            </div>
          </div>
        )}

        {/* API Key and Usage Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* API Key Card */}
          <Card 
            className="relative card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center text-netural-800"><Key className="h-4 w-4 mr-1" /> API Key</CardTitle>
              <CardDescription className="text-sm">
                Your project's API key for integration
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 min-w-0">
                  <div className="bg-muted rounded-lg p-2 font-mono text-sm border">
                    {truncateApiKey(currentProject.api_key)}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyApiKey}
                  className="flex items-center space-x-1"
                >
                  {apiKeyCopied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  <span>{apiKeyCopied ? 'Copied!' : 'Copy'}</span>
                </Button>
              </div>
              {/* Copied animation */}
              {apiKeyCopied && (
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-md text-sm font-medium animate-fade-in-out">
                  Copied!
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Card */}
          {usageInfo && (
            <Card 
              className="card-premium-hover"
              style={{
                opacity: 0,
                animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards',
              }}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center text-netural-800"><Gauge className="h-4 w-4 mr-1" /> {usageInfo.label}</CardTitle>
                <CardDescription className="text-sm">{usageInfo.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xl font-bold">
                      {formatNumber(usageInfo.used)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      of {usageInfo.total ? formatNumber(usageInfo.total) : '∞'}
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        usageInfo.total && usageInfo.used / usageInfo.total > 0.8 
                          ? 'bg-red-500' 
                          : usageInfo.total && usageInfo.used / usageInfo.total > 0.6 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                      }`}
                      style={{ 
                        width: usageInfo.total ? `${Math.min((usageInfo.used / usageInfo.total) * 100, 100)}%` : '50%' 
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>



        {/* Main Dashboard Content */}
        <Card 
          className="overflow-hidden"
          style={{
            opacity: 0,
            animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 200ms forwards',
          }}
        >
          <CardHeader className="border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl flex items-center">All Topics
                  <Badge variant="secondary" className="text-xs ml-2">
                    {sortedAndFilteredClusters.length} topics
                  </Badge>
                </CardTitle>
                <CardDescription>
                  View all topics across all events with filtering options
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground mt-1">
                    <Eye className="h-3 w-3" />
                    <span>Click any row to view all values</span>
                  </div>
                </CardDescription>
              </div>
              <div className="flex items-center space-x-3">
                {/* Event Type Filter Dropdown */}
                <div className="relative">
                  <Select value={filterEventType} onValueChange={setFilterEventType}>
                    <SelectTrigger className="w-48 pl-10 h-10">
                      <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <SelectValue placeholder="All events" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All events</SelectItem>
                      {uniqueEventTypes.map((eventType) => (
                        <SelectItem key={eventType} value={eventType}>
                          {eventType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Metadata Key Filter Dropdown */}
                <div className="relative">
                  <Select value={filterKey} onValueChange={setFilterKey}>
                    <SelectTrigger className="w-48 pl-10 h-10">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <SelectValue placeholder="All keys" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All keys</SelectItem>
                      {uniqueKeys.map((key) => (
                        <SelectItem key={key} value={key}>
                          {key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Label Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search topic labels..."
                    value={searchLabel}
                    onChange={(e) => setSearchLabel(e.target.value)}
                    className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background h-10"
                  />
                </div>
                
                {/* Trend time range selector */}
                <div className="relative">
                  <Select value={trendDays.toString()} onValueChange={(v) => handleTrendDaysChange(parseInt(v) as TrendDays)}>
                    <SelectTrigger className="w-32 h-10">
                      <TrendingUp className="h-4 w-4 mr-2 text-muted-foreground" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TREND_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filters button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSegmentsPanelOpen(true)}
                  className="h-10 px-3"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                  {Object.keys(segmentFilters).length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {Object.keys(segmentFilters).length}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          
          {/* Table Content */}
          {clustersLoading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : clusters.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <h4 className="text-lg font-medium mb-2">No topics found</h4>
              <p className="text-sm">
                No topic analysis available for this project
              </p>
            </div>
          ) : sortedAndFilteredClusters.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <h4 className="text-lg font-medium mb-2">No matches found</h4>
              <p className="text-sm">
                {filterEventType && filterEventType !== 'all' && filterKey && filterKey !== 'all' && searchLabel
                  ? `No topics for event "${filterEventType}" with key "${filterKey}" and label containing "${searchLabel}"`
                  : filterEventType && filterEventType !== 'all' && filterKey && filterKey !== 'all'
                  ? `No topics for event "${filterEventType}" with key "${filterKey}"`
                  : filterEventType && filterEventType !== 'all' && searchLabel
                  ? `No topics for event "${filterEventType}" with label containing "${searchLabel}"`
                  : filterEventType && filterEventType !== 'all'
                  ? `No topics for event "${filterEventType}"`
                  : filterKey && filterKey !== 'all' && searchLabel
                  ? `No topics with key "${filterKey}" and label containing "${searchLabel}"`
                  : filterKey && filterKey !== 'all'
                  ? `No topics with key "${filterKey}"` 
                  : searchLabel
                  ? `No topics with label containing "${searchLabel}"`
                  : 'No topics match your filter criteria'
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-semibold">Topic Label</TableHead>
                  <TableHead className="font-semibold">Event</TableHead>
                  <TableHead className="font-semibold">Key</TableHead>
                  <SortableHeader field="size">Events</SortableHeader>
                  <SortableHeader field="delta">{trendDays}d Change</SortableHeader>
                  <TableHead className="font-semibold">Trend ({trendDays}d)</TableHead>
                  <TableHead className="font-semibold">Sample Values</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedAndFilteredClusters.map((cluster, index) => (
                  <TableRow 
                    key={`${cluster.event_type}-${cluster.id}`} 
                    className="hover:bg-muted/20 transition-all cursor-pointer" 
                    style={{
                      opacity: 0,
                      animation: `tableRowFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${Math.min(index * 30, 600)}ms forwards`,
                    }}
                    onClick={() => handleClusterClick(cluster.id)}
                  >
                    <TableCell>
                      <div className="font-medium text-sm">{cluster.label}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs rounded-md shadow-sm whitespace-nowrap">
                        {cluster.event_type || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs rounded-md shadow-sm">
                        {cluster.key}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-medium">
                        {formatNumber(cluster.size)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {cluster.delta != null ? (
                        <div className={`flex items-center space-x-1 ${
                          cluster.delta > 0 ? 'text-green-600' : cluster.delta < 0 ? 'text-red-600' : 'text-muted-foreground'
                        }`}>
                          {cluster.delta > 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : cluster.delta < 0 ? (
                            <TrendingDown className="h-4 w-4" />
                          ) : null}
                          <span className="text-sm font-medium">
                            {cluster.delta > 0 ? '+' : ''}{cluster.delta.toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-muted-foreground text-xs cursor-help">
                              {cluster.has_trend_data === false ? 'New' : 'Stable'}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{cluster.has_trend_data === false 
                              ? `Topic created within the last ${trendDays} days` 
                              : 'No activity in the first half of the period to compare against'}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {cluster.sparkline && cluster.sparkline.length > 0 && cluster.sparkline.some(v => v > 0) ? (
                        <Sparkline 
                          data={cluster.sparkline} 
                          positive={cluster.delta != null ? cluster.delta >= 0 : true} 
                        />
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-muted-foreground text-xs cursor-help">
                              {cluster.has_trend_data === false ? 'New topic' : 'No recent activity'}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{cluster.has_trend_data === false 
                              ? `Topic created within the last ${trendDays} days` 
                              : `No events tracked in the last ${trendDays} days`}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {cluster.samples.slice(0, 3).map((sample, sampleIndex) => (
                          <Tooltip key={sampleIndex}>
                            <TooltipTrigger asChild>
                              <div className="text-xs bg-muted/60 rounded px-2 py-1 font-mono truncate max-w-32 cursor-help">
                                {sample}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm">
                              <p className="break-words">{sample}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                        {cluster.samples.length > 3 && (
                          <div className="text-xs text-muted-foreground">
                            +{cluster.samples.length - 3} more
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
        
        {/* Cluster Values Sheet */}
        <ClusterValuesSheet
          isOpen={isClusterSheetOpen}
          onClose={handleClusterSheetClose}
          clusterId={selectedClusterId}
          projectId={currentProject?.id || null}
          token={token}
        />

        {/* Segments Panel */}
        <SegmentsPanel
          isOpen={isSegmentsPanelOpen}
          onClose={() => setIsSegmentsPanelOpen(false)}
          projectId={currentProject?.id || 0}
          token={token}
          activeFilters={segmentFilters}
          onFiltersChange={handleSegmentFiltersChange}
        />
      </div>
    </div>
    </TooltipProvider>
  );
} 